import { springApiClient } from '../spring-boot-client';
import type { Order, ApiOrder, OrderStatus, OrderItem, CreateOrderRequest, UpdateOrderRequest, CancelOrderRequest, Payment } from '../../types';

export interface OrdersResponse {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
}

interface ApiOrdersResponse {
  orders: ApiOrder[];
  total: number;
  page: number;
  limit: number;
}

interface ApiOrderDetailResponse {
  order: ApiOrder;
  items: OrderItem[];
  payment?: Payment | null;
}

export interface OrderDetailResponse {
  order: ApiOrder;
  items: OrderItem[];
  payment?: Payment;
}

function mapApiOrderToOrder(apiOrder: ApiOrder): Order {
  return {
    id: apiOrder.id,
    orderNumber: apiOrder.external_order_id || apiOrder.id.slice(0, 8).toUpperCase(),
    customer: apiOrder.customer_name || 'N/A',
    email: apiOrder.customer_email || 'N/A',
    items: 0,
    total: apiOrder.total_amount,
    status: apiOrder.status,
    date: apiOrder.created_at,
    externalOrderId: apiOrder.external_order_id,
    currency: apiOrder.currency,
  };
}

export const ordersService = {
  getOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: OrderStatus;
    customer_email?: string;
  }): Promise<OrdersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.customer_email) queryParams.append('customer_email', params.customer_email);

    const queryString = queryParams.toString();
    const endpoint = `/orders${queryString ? `?${queryString}` : ''}`;

    const response = await springApiClient.get<ApiOrdersResponse>(endpoint);
    return {
      orders: response.orders.map(mapApiOrderToOrder),
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  },

  getOrder: async (id: string): Promise<OrderDetailResponse> => {
    const response = await springApiClient.get<ApiOrderDetailResponse>(`/orders/${id}`);
    return {
      order: response.order,
      items: response.items,
      payment: response.payment || undefined,
    };
  },

  createOrder: async (data: CreateOrderRequest): Promise<Order> => {
    const response = await springApiClient.post<{ order: ApiOrder }>(`/orders`, data);
    return mapApiOrderToOrder(response.order);
  },

  updateOrder: async (id: string, data: Omit<UpdateOrderRequest, 'idempotency_key'>): Promise<Order> => {
    const idempotencyKey = crypto.randomUUID();
    const response = await springApiClient.put<{ order: ApiOrder }>(`/orders/${id}`, {
      ...data,
      idempotency_key: idempotencyKey,
    } as UpdateOrderRequest);
    return mapApiOrderToOrder(response.order);
  },

  cancelOrder: async (id: string, reason: string): Promise<Order> => {
    const idempotencyKey = crypto.randomUUID();
    const response = await springApiClient.post<{ order: ApiOrder }>(`/orders/${id}/cancel`, {
      reason,
      idempotency_key: idempotencyKey,
    } as CancelOrderRequest);
    return mapApiOrderToOrder(response.order);
  },

  deleteOrder: (id: string): Promise<void> => {
    return springApiClient.delete<void>(`/orders/${id}`);
  },
};
